import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useHasPermission } from '@/hooks/usePermissions';
import { EstimateDocTemplateEditor } from '@/components/documents/estimates/templateBuilder/EstimateDocTemplateEditor';
import {
  useEstimateDocTemplate,
  useUpdateEstimateDocTemplate,
  usePublishEstimateDocTemplate,
  useCloneEstimateDocTemplate,
} from '@/hooks/estimateDocTemplates';

export const Route = createFileRoute('/_dashboard/dashboard/settings/document-templates/$id')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('estimates:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  component: EditDocumentTemplatePage,
});

function EditDocumentTemplatePage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const navigate = useNavigate();
  // Mirror the backend guards: clone/publish/update all require estimates:edit.
  const canEdit = useHasPermission('estimates:edit');
  const { data: tpl, isLoading, isError } = useEstimateDocTemplate(id);
  const update = useUpdateEstimateDocTemplate();
  const publish = usePublishEstimateDocTemplate();
  const clone = useCloneEstimateDocTemplate();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (isError || !tpl) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-muted-foreground">
        This design could not be loaded.
      </div>
    );
  }

  const readOnly = tpl.isSystemDefault || !canEdit;
  const duplicate = () =>
    clone.mutateAsync({ id }).then((row) =>
      navigate({
        to: '/dashboard/settings/document-templates/$id',
        params: { id: row.id },
      }),
    );

  return (
    <EstimateDocTemplateEditor
      mode="edit"
      initialName={tpl.name}
      initialLayout={tpl.layout}
      readOnly={readOnly}
      status={tpl.status}
      hasUnpublishedChanges={tpl.hasUnpublishedChanges}
      saving={update.isPending}
      publishing={publish.isPending}
      onBack={() => router.history.back()}
      onSave={
        readOnly
          ? () => undefined
          : (name, layout) => update.mutateAsync({ id, dto: { name, ...layout } })
      }
      onPublish={readOnly ? undefined : () => publish.mutate({ id })}
      onDuplicate={canEdit ? duplicate : undefined}
      onUseInBu={() => navigate({ to: '/dashboard/settings/trades' })}
    />
  );
}
